import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { portalDs, PortalDsAlert } from 'src/components/portal-design-system';
import { FilterBar, MetricCard, MetricCardGrid } from 'src/components/shared';
import { cn } from 'src/lib/utils';
import { PortalSyncNotConfiguredBanner } from './components/portal-sync-not-configured-banner';
import { TeamDetailDrawer } from './components/team-detail-drawer';
import { TeamsTable } from './components/teams-table';
import { UserDetailDrawer } from './components/user-detail-drawer';
import { UsersTable } from './components/users-table';
import { UsersTeamsToggle } from './components/users-teams-toggle';
import { useGetPortalMetricsQuery } from './endpoints/portal-users.endpoints';
import { useFlowTeams } from './hooks/use-flow-teams';
import { useFlowUsers } from './hooks/use-flow-users';
import { useUsersTabUrlParams } from './hooks/use-users-tab-url-params';
import { workspaceMemberToPortalUser, mapPortalMetrics } from './utils/portal-users.mappers';
import type { PortalWorkspaceMember, StatusFilter, SubscriptionFilter } from './types/users.types';

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'blocked', label: 'Blocked' },
] as const;

const SUBSCRIPTION_FILTER_OPTIONS = [
  { value: 'all', label: 'All tiers' },
  { value: 'free', label: 'Free' },
  { value: 'basic', label: 'Basic' },
  { value: 'pro', label: 'Pro' },
  { value: 'media_partner', label: 'Media partner' },
  { value: 'internal', label: 'Internal' },
] as const;

export function UsersTab() {
  const {
    view,
    setView,
    selectedUserId,
    setSelectedUserId,
    selectedWorkspaceId,
    setSelectedWorkspaceId,
    openTeamView,
  } = useUsersTabUrlParams();

  const {
    users,
    totalCount,
    page,
    pageSize,
    setPage,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    subscriptionFilter,
    setSubscriptionFilter,
    showGuestAccounts,
    setShowGuestAccounts,
    isLoading,
    isError,
    isPortalSyncNotConfigured,
    applyBlockAction,
    applyUnblockAction,
    applyEmailVerificationBypass,
    addTokens,
    setBillingConversionDisabledForUser,
    setConversionModalHiddenForUser,
    saveSubscriptionTier,
    actionsDisabled,
  } = useFlowUsers();

  const {
    teams,
    totalCount: teamsTotalCount,
    page: teamsPage,
    pageSize: teamsPageSize,
    setPage: setTeamsPage,
    search: teamsSearch,
    setSearch: setTeamsSearch,
    subscriptionFilter: teamsSubscriptionFilter,
    setSubscriptionFilter: setTeamsSubscriptionFilter,
    isLoading: isTeamsLoading,
    isError: isTeamsError,
    isPortalSyncNotConfigured: isTeamsPortalSyncNotConfigured,
    applyBulkSettings,
    actionsDisabled: teamsActionsDisabled,
  } = useFlowTeams();

  const {
    data: metricsData,
    isLoading: isMetricsLoading,
    isError: isMetricsError,
  } = useGetPortalMetricsQuery();

  const [selectedMemberFromTeam, setSelectedMemberFromTeam] = useState<PortalWorkspaceMember | null>(null);

  const metrics = metricsData ? mapPortalMetrics(metricsData) : null;
  const selectedListUser = users.find((user) => user.id === selectedUserId) ?? null;
  const selectedListTeam = teams.find((team) => team.workspace_id === selectedWorkspaceId) ?? null;

  const isTeamsView = view === 'teams';
  const activeSearch = isTeamsView ? teamsSearch : search;
  const setActiveSearch = isTeamsView ? setTeamsSearch : setSearch;
  const activeSubscriptionFilter = isTeamsView ? teamsSubscriptionFilter : subscriptionFilter;
  const setActiveSubscriptionFilter = isTeamsView
    ? setTeamsSubscriptionFilter
    : setSubscriptionFilter;
  const activeIsLoading = isTeamsView ? isTeamsLoading : isLoading;
  const activeIsError = isTeamsView ? isTeamsError : isError;
  const activeIsPortalSyncNotConfigured = isTeamsView
    ? isTeamsPortalSyncNotConfigured
    : isPortalSyncNotConfigured;

  const handleOpenTeam = (workspaceId: string) => {
    setSelectedUserId(null);
    openTeamView(workspaceId);
  };

  const handleMemberClick = (member: PortalWorkspaceMember) => {
    setSelectedMemberFromTeam(member);
    setSelectedUserId(member.id);
  };

  const selectedMemberListUser = selectedMemberFromTeam && selectedWorkspaceId
    ? workspaceMemberToPortalUser(selectedMemberFromTeam, { workspace_id: selectedWorkspaceId })
    : null;

  return (
    <div className={cn(portalDs.page, 'h-full overflow-y-auto px-6 py-8 sm:px-10')}>
      {isMetricsLoading ? (
        <div className="mb-6 flex items-center gap-2 text-[#5E6278]">
          <Loader2
            className="size-4 animate-spin"
            aria-hidden
          />
          <span className={portalDs.caption}>
            Loading dashboard metrics…
          </span>
        </div>
      ) : isMetricsError && !isPortalSyncNotConfigured ? (
        <PortalDsAlert
          variant="danger"
          title="Could not load dashboard metrics"
          className="mb-6"
        >
          Metrics are temporarily unavailable. Try refreshing the page.
        </PortalDsAlert>
      ) : metrics ? (
        <MetricCardGrid>
          <MetricCard
            value={metrics.newUsersLast30d}
            label="New users (last 30 days)"
            delta={{
              value: metrics.newUsersDelta,
              direction: metrics.newUsersDeltaDirection,
            }}
          />
          <MetricCard
            value={metrics.creativesGeneratedLast30d}
            label="Creatives generated (last 30 days)"
          />
          <MetricCard
            value={metrics.brandsIngestedLast30d}
            label="Brands ingested (last 30 days)"
          />
          <MetricCard
            value={metrics.guestSignupsLast30d}
            label="Guest signups (30d)"
          />
          <MetricCard
            value={metrics.guestAccountsTotal}
            label="Total guest accounts"
          />
          <MetricCard
            value={metrics.guestAccountsUnclaimed}
            label="Unclaimed guest accounts"
          />
        </MetricCardGrid>
      ) : null}

      <div className="mb-4">
        <UsersTeamsToggle
          value={view}
          onChange={setView}
        />
      </div>

      <FilterBar
        searchValue={activeSearch}
        searchPlaceholder={isTeamsView ? 'Search teams by name' : 'Search users by name or email'}
        onSearchChange={setActiveSearch}
        selects={[
          ...(isTeamsView ? [] : [{
            id: 'status',
            label: 'Status',
            value: statusFilter,
            options: [...STATUS_FILTER_OPTIONS],
            onChange: (value: string) => setStatusFilter(value as StatusFilter),
          }]),
          {
            id: 'subscription',
            label: 'Subscription',
            value: activeSubscriptionFilter,
            options: [...SUBSCRIPTION_FILTER_OPTIONS],
            onChange: (value) => setActiveSubscriptionFilter(value as SubscriptionFilter),
          },
        ]}
        toggles={isTeamsView ? [] : [
          {
            id: 'guest-accounts',
            label: 'Show guest accounts',
            checked: showGuestAccounts,
            onChange: setShowGuestAccounts,
          },
        ]}
      />

      {activeIsPortalSyncNotConfigured ? (
        <PortalSyncNotConfiguredBanner />
      ) : activeIsLoading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-[#5E6278]">
          <Loader2
            className="size-5 animate-spin"
            aria-hidden
          />
          <span>
            {isTeamsView ? 'Loading teams…' : 'Loading users…'}
          </span>
        </div>
      ) : activeIsError ? (
        <PortalDsAlert
          variant="danger"
          title={isTeamsView ? 'Could not load teams' : 'Could not load users'}
        >
          {isTeamsView
            ? 'Team data is temporarily unavailable. Try refreshing the page.'
            : 'User data is temporarily unavailable. Try refreshing the page.'}
        </PortalDsAlert>
      ) : isTeamsView ? (
        <TeamsTable
          teams={teams}
          totalCount={teamsTotalCount}
          page={teamsPage}
          pageSize={teamsPageSize}
          onPageChange={setTeamsPage}
          onRowClick={(team) => setSelectedWorkspaceId(team.workspace_id)}
        />
      ) : (
        <UsersTable
          users={users}
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          showGuestBadge={showGuestAccounts}
          onPageChange={setPage}
          onRowClick={(user) => {
            setSelectedMemberFromTeam(null);
            setSelectedUserId(user.id);
          }}
        />
      )}

      <TeamDetailDrawer
        workspaceId={selectedWorkspaceId}
        open={Boolean(selectedWorkspaceId) && !selectedUserId}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedWorkspaceId(null);
          }
        }}
        listTeam={selectedListTeam}
        actionsDisabled={teamsActionsDisabled}
        onMemberClick={handleMemberClick}
        onApplyBulkSettings={applyBulkSettings}
      />

      <UserDetailDrawer
        userId={selectedUserId}
        open={Boolean(selectedUserId)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedUserId(null);
            setSelectedMemberFromTeam(null);
          }
        }}
        listUser={selectedListUser ?? selectedMemberListUser}
        actionsDisabled={actionsDisabled}
        onSuspend={(userId) => applyBlockAction(userId)}
        onReactivate={(userId) => applyUnblockAction(userId)}
        onBypassEmailVerification={(userId) => void applyEmailVerificationBypass(userId)}
        onSetBillingConversionDisabled={(userId, value) => void setBillingConversionDisabledForUser(userId, value)}
        onSetConversionModalHidden={(userId, value) => void setConversionModalHiddenForUser(userId, value)}
        onAddTokens={addTokens}
        onSaveSubscriptionTier={saveSubscriptionTier}
        onOpenTeam={handleOpenTeam}
      />
    </div>
  );
}
